/**
 * Portable evidence — the vocabulary and the wire contract. (W5.8 · F31 Phase 3)
 *
 * ── The problem this names ───────────────────────────────────────────────
 *
 * Once one journey is shared by several companies, "the evidence company A saw
 * when it decided" and "the citizen's current artefacts" are different things.
 * Every drawer read is LIVE: a recompute triggered by company B, a pitch video
 * the citizen re-records, a TestIt result that expires — each silently rewrites
 * what company A's past decision appears to rest on.
 *
 * An EVIDENCE CAPTURE is company A's copy of what it could see at the moment it
 * decided. It binds to the ENGAGEMENT (DW5-5) — the (company, journey)
 * relationship — never to the journey or the talent, which several companies
 * share. It is append-only and enforced so by database triggers (migration
 * 0131), not by convention.
 *
 * The bare word "snapshot" is deliberately not used for any of this: it already
 * means three different things in this codebase (the client-supplied
 * `testit_verdicts.snapshot`, the cohort snapshot export, and the rerun
 * `calculatedAt` snapshot on the Vetting drawer).
 *
 * ── D1 · one kind, exactly one consent scope ─────────────────────────────
 *
 * {@link EVIDENCE_KIND_CONSENT_SCOPE} is the ONE map from evidence kind to the
 * consent scope that governs it. It is checked with `satisfies`, so adding a
 * kind without a scope — or a scope entry for a kind that does not exist — is a
 * compile error, not a review comment. A kind with no scope cannot be captured
 * because it cannot be written down here.
 *
 * ── D4 · consent is decided at CAPTURE ───────────────────────────────────
 *
 * An item whose scope was not granted when the decision was taken is stored as
 * a `withheld` marker — never omitted, because a silently shorter evidence pack
 * is indistinguishable from a citizen who did less. A later revocation does NOT
 * blind an existing capture; it governs only later captures and live reads.
 */

import type { ConsentScope } from './consent-scopes';
import { SPONSORED_SUBSTAGES } from './pipeline';

// ═══════════════════════════════════════════════════════════════════════════
// EVIDENCE KINDS — the inventory
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Every artefact class a company's decision can rest on, one per canonical
 * reader output. A reader output that spans two consent scopes is split so each
 * kind has exactly one (the Mobility drawer carries household members; the Care
 * drawer carries the check-in diary) — a single item governed by two scopes
 * could not be withheld for one and captured for the other.
 *
 * JOURNEY-SCOPED — read through the engagement's journey:
 *   checkup_results · checkup_progress · mobility_plan · adaptation_progress ·
 *   care_overview · mobility_documents
 *
 * TALENT-SCOPED — the citizen's own record, shared by every company at once, so
 * the class that changes under a decision most often:
 *   testit_results · passport_evidence · pitch_video · cv · household ·
 *   care_checkins
 *
 * `mobility_documents` is the FILES behind the Mobility drawer's documents
 * (passport, visa, right-to-work papers): `mobility_plan` carries what the
 * company could see about each document, this kind holds the documents
 * themselves by reference — so a sponsoring employer's copy is kept by ITS
 * record, on ITS retention clock (D2/D7), when the citizen erases their vault.
 *
 * New kinds are APPENDED: the order is the order items are returned in.
 *
 * The inventory, the surfaces deliberately NOT captured, and why, are recorded
 * in docs/01-platform/_deprecated/2026-10/HOTFIX-2026.9.14-W5-PLAN.md §8.
 */
export const EVIDENCE_KINDS = [
  'checkup_results',
  'checkup_progress',
  'mobility_plan',
  'adaptation_progress',
  'care_overview',
  'testit_results',
  'passport_evidence',
  'pitch_video',
  'cv',
  'household',
  'care_checkins',
  'mobility_documents',
] as const;

export type EvidenceKind = (typeof EVIDENCE_KINDS)[number];

/**
 * D1 — the ONE map. Compile-time exhaustive in both directions: a missing kind
 * fails `satisfies Record<EvidenceKind, …>`, an unknown key fails the excess
 * property check on the literal.
 */
export const EVIDENCE_KIND_CONSENT_SCOPE = {
  checkup_results: 'checkup',
  checkup_progress: 'checkup',
  mobility_plan: 'teleportal',
  adaptation_progress: 'qultureup',
  care_overview: 'care',
  testit_results: 'testit',
  passport_evidence: 'passport_stamps',
  pitch_video: 'pitch_video',
  cv: 'cv',
  household: 'household',
  care_checkins: 'care_checkins',
  mobility_documents: 'teleportal',
} as const satisfies Record<EvidenceKind, ConsentScope>;

/**
 * Kinds whose evidence IS a stored file. Their items hold a REFERENCE to the
 * vault object — never a copy (DW5-5) — which is what makes a citizen's delete
 * a retention question rather than a storage one (D2).
 */
export const EVIDENCE_BLOB_KINDS = [
  'pitch_video',
  'cv',
  'mobility_documents',
] as const satisfies readonly EvidenceKind[];

export type EvidenceBlobKind = (typeof EVIDENCE_BLOB_KINDS)[number];

/** The i18n key a client renders for a kind. Literal, so the key gate can see it. */
export const EVIDENCE_KIND_LABEL_KEYS = {
  checkup_results: 'evidence.kind.checkupResults',
  checkup_progress: 'evidence.kind.checkupProgress',
  mobility_plan: 'evidence.kind.mobilityPlan',
  adaptation_progress: 'evidence.kind.adaptationProgress',
  care_overview: 'evidence.kind.careOverview',
  testit_results: 'evidence.kind.testitResults',
  passport_evidence: 'evidence.kind.passportEvidence',
  pitch_video: 'evidence.kind.pitchVideo',
  cv: 'evidence.kind.cv',
  household: 'evidence.kind.household',
  care_checkins: 'evidence.kind.careCheckins',
  mobility_documents: 'evidence.kind.mobilityDocuments',
} as const satisfies Record<EvidenceKind, string>;

/**
 * D5 — WHAT A CAPTURE MAY NOT BE A FUNCTION OF: THE CLOCK.
 *
 * A capture must be a function of the FACTS. Anything computed from "now"
 * moves on its own, so a diff run a day later would report `different` with
 * nothing written — and `changedAt`, read from the payload, would point at a
 * date BEFORE the decision. Two classes, and every kind was audited for both:
 *
 * DERIVED — recomputable from facts the payload already holds, so they are NOT
 * stored. Each side computes them with its own clock: the record with its
 * `captured_at` (what the company saw), the current read with `now`.
 *
 * AS-OF — an observation over a window the payload cannot reproduce (a 14-day
 * mood average, a streak ending today, miles this week). They ARE stored, so
 * the record still shows what the company saw, and they are excluded from the
 * comparison: a window sliding is not a change in the talent's record.
 *
 * Dotted paths; `[]` means "every element of this array".
 */
export const EVIDENCE_DERIVED_FIELDS = {
  checkup_results: [],
  checkup_progress: [],
  // Overdue is `dueDate < now` over tasks the payload carries.
  mobility_plan: ['overall.overdueTasks'],
  adaptation_progress: [],
  care_overview: [],
  // Validity is `expiresAt` against a clock; `done`/`completed` follow from it.
  testit_results: [
    'completed',
    'invitedTests[].done',
    'invitedTests[].expiresAt',
    'invitedTests[].daysRemaining',
    'results[].isValid',
    'results[].isReusable',
    'results[].daysRemaining',
  ],
  passport_evidence: [],
  pitch_video: [],
  cv: [],
  household: [],
  care_checkins: [],
  mobility_documents: [],
} as const satisfies Record<EvidenceKind, readonly string[]>;

export const EVIDENCE_AS_OF_FIELDS = {
  checkup_results: [],
  checkup_progress: [],
  mobility_plan: [],
  // Streak and the "this week" series are anchored on the day they are read.
  adaptation_progress: [
    'overall.streakDays',
    'overall.weeklyMissionsCompleted',
    'overall.weeklyProgress',
    'streakDays',
  ],
  // The commitment index is recomputed over a rolling 90-day window and
  // re-stamped when its cache lapses.
  care_overview: ['commitment'],
  testit_results: [],
  // "Miles this week" is a rolling 7-day sum.
  passport_evidence: ['milesThisWeek'],
  pitch_video: [],
  cv: [],
  household: [],
  // The thermometer and mood average run over 14 days, the streak ends today,
  // and `last30Days` is a 30-day count. `totalCheckins` and `lastCheckinAt`
  // are facts, and stay comparable.
  care_checkins: [
    'wellbeingScore',
    'averageMood',
    'moodTrend',
    'summary.last30Days',
    'summary.currentStreak',
    'summary.moodTrend',
  ],
  mobility_documents: [],
} as const satisfies Record<EvidenceKind, readonly string[]>;

export function isEvidenceKind(value: string): value is EvidenceKind {
  return (EVIDENCE_KINDS as readonly string[]).includes(value);
}

// ═══════════════════════════════════════════════════════════════════════════
// TRIGGERS, ORIGINS, DISPOSITIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * What caused a capture. Each is taken INSIDE the causing transaction, so a
 * capture that fails rolls the decision back with it. Recompute completion is
 * deliberately not a trigger: it has no engagement dimension, and would mint one
 * capture per company with no decision attached to any of them.
 */
export const EVIDENCE_TRIGGERS = [
  'stage_transition',
  'verdict',
  'sponsorship_activation',
  'engagement_end',
  'engagement_start',
  'on_demand',
] as const;

export type EvidenceTrigger = (typeof EVIDENCE_TRIGGERS)[number];

export const EVIDENCE_TRIGGER_LABEL_KEYS = {
  stage_transition: 'evidence.trigger.stageTransition',
  engagement_start: 'evidence.trigger.engagementStart',
  verdict: 'evidence.trigger.verdict',
  sponsorship_activation: 'evidence.trigger.sponsorshipActivation',
  engagement_end: 'evidence.trigger.engagementEnd',
  on_demand: 'evidence.trigger.onDemand',
} as const satisfies Record<EvidenceTrigger, string>;

/**
 * Where a capture came from. `backfill` is migration 0133's record of a TestIt
 * verdict decided before server capture existed: its only evidence is what the
 * CLIENT sent, and the consent in force at that moment was never recorded — so
 * it says so rather than pretending to be a live capture.
 */
export const EVIDENCE_ORIGINS = ['live', 'backfill'] as const;
export type EvidenceOrigin = (typeof EVIDENCE_ORIGINS)[number];

/**
 * An item's state.
 *
 *   captured               — read by the canonical reader under a granted scope.
 *   withheld               — the scope was NOT granted when the decision was
 *                            taken (D4). Visible, never omitted.
 *   legacy_client_supplied — backfill only: what the browser sent with a TestIt
 *                            verdict before this store existed. Unvalidated, and
 *                            labelled as such.
 *   redacted               — removed after the fact by the controller; the one
 *                            UPDATE the immutability trigger admits.
 */
export const EVIDENCE_DISPOSITIONS = [
  'captured',
  'withheld',
  'legacy_client_supplied',
  'redacted',
] as const;

export type EvidenceDisposition = (typeof EVIDENCE_DISPOSITIONS)[number];

/** The reason a withheld marker carries. One reason today; a key, never prose. */
export const EVIDENCE_WITHHELD_REASON_KEY = 'evidence.withheld.scopeNotGranted';

// ═══════════════════════════════════════════════════════════════════════════
// RETENTION (D6 · D7)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Why a company still holds a decision record, and so how long.
 *
 *   decision_in_force — the relationship is live and its latest decision still
 *                       stands. Retained for the configured BOUNDED period from
 *                       the engagement's newest capture (W8.D). It used to
 *                       carry no end date at all, which meant the sweep — whose
 *                       predicate is `retain_until IS NOT NULL` — could never
 *                       see these rows, and the citizen's Art. 12(4) notice
 *                       told them the company kept their file indefinitely.
 *                       "No outcome yet" is a reason the clock has not STARTED
 *                       from an outcome, not a reason there is no clock.
 *   not_progressed    — the company declined or archived. Retained for the
 *                       configured period after THAT decision.
 *   engagement_ended  — a sponsored/hired relationship is over. Retained for
 *                       the configured period after the end (the one-year
 *                       limitation on labour claims, D7).
 */
export const EVIDENCE_RETENTION_BASES = [
  'decision_in_force',
  'not_progressed',
  'engagement_ended',
] as const;

export type EvidenceRetentionBasis = (typeof EVIDENCE_RETENTION_BASES)[number];

/** What a citizen is told, per basis (Art. 12(4)). Keys, never prose. */
export const EVIDENCE_RETENTION_REASON_KEYS = {
  decision_in_force: 'evidence.retention.reason.decisionInForce',
  not_progressed: 'evidence.retention.reason.notProgressed',
  engagement_ended: 'evidence.retention.reason.engagementEnded',
} as const satisfies Record<EvidenceRetentionBasis, string>;

/** Why a controller redacted an item. The vocabulary the redact API accepts. */
export const EVIDENCE_REDACTION_REASONS = [
  'data_subject_request',
  'legal_obligation',
  'controller_minimisation',
] as const;

export type EvidenceRedactionReason = (typeof EVIDENCE_REDACTION_REASONS)[number];

export const EVIDENCE_REDACTION_REASON_KEYS = {
  data_subject_request: 'evidence.redaction.reason.dataSubjectRequest',
  legal_obligation: 'evidence.redaction.reason.legalObligation',
  controller_minimisation: 'evidence.redaction.reason.controllerMinimisation',
} as const satisfies Record<EvidenceRedactionReason, string>;

// ═══════════════════════════════════════════════════════════════════════════
// THE DECISION A CAPTURE RECORDS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `journey_pipeline_stage` — mirrored here because the decision record crosses
 * the wire. A platform-api spec asserts this list equals the Postgres enum, so
 * the two cannot drift silently.
 */
export const EVIDENCE_PIPELINE_STAGES = [
  'acquisition',
  'vetting',
  ...SPONSORED_SUBSTAGES,
  'archived',
] as const;

export type EvidencePipelineStage = (typeof EVIDENCE_PIPELINE_STAGES)[number];

/**
 * The decision itself, discriminated by trigger. Stored on the capture header,
 * immutable with it.
 */
export type EvidenceDecision =
  | {
      trigger: 'stage_transition';
      fromStage: EvidencePipelineStage | null;
      toStage: EvidencePipelineStage;
      /** The `pipeline_transitions` row written in the same transaction, if any. */
      transitionId: string | null;
    }
  | {
      trigger: 'verdict';
      verdict: 'passed' | 'declined';
      /**
       * The verdict this one replaced on the same engagement (D3). On a live
       * capture `null` means none. On a `backfill` capture it is always `null`
       * and means NOT RECORDED: the replaced value was overwritten in place
       * before this store existed. Read `origin` before reading this.
       */
      replaced: 'passed' | 'declined' | null;
      verdictId: string;
    }
  | { trigger: 'sponsorship_activation'; sponsorshipId: string }
  | { trigger: 'engagement_end'; endReason: string }
  /**
   * The moment a company GAINED access to a citizen — an accepted invitation
   * that moved no stage (a TestIt-only relationship accepts into `vetting`
   * from `vetting`). Without it, such a relationship held no record at all
   * until somebody thought to take one, which is the opposite of the point.
   */
  | { trigger: 'engagement_start'; invitationId: string; source: 'invitation_accept' }
  | { trigger: 'on_demand' };

// ═══════════════════════════════════════════════════════════════════════════
// WIRE CONTRACT — what platform-api returns and @tonnie/sdk consumes
// ═══════════════════════════════════════════════════════════════════════════

/** A retained file an item references. Never the storage key — that stays server-side. */
export interface EvidenceBlobView {
  /** Pass to the retained-blob URL endpoint. */
  blobRefId: string;
  vaultFileId: string;
  byteSize: number;
}

export interface EvidenceItemView {
  id: string;
  kind: EvidenceKind;
  labelKey: string;
  consentScope: ConsentScope;
  disposition: EvidenceDisposition;
  /**
   * The canonical reader's output at decision time. `null` for `withheld` and
   * `redacted`. A `captured` item whose reader found nothing carries JSON
   * `null` here too — "nothing existed" is data, "withheld" is a disposition,
   * and the two are never conflated.
   */
  payload: unknown;
  /** SHA-256 of the canonical JSON of `payload`. Null when there is no payload. */
  payloadSha256: string | null;
  /** Set only on `withheld`. */
  withheldReasonKey: string | null;
  /**
   * Set when a viewer-level rule (BL-122 Care risk signals) hid part of a
   * captured payload FROM THIS VIEWER. The stored record is unchanged.
   */
  viewerRedactedKeys: string[];
  redactedAt: string | null;
  redactionReasonKey: string | null;
  blobs: EvidenceBlobView[];
}

export interface EvidenceCaptureSummary {
  id: string;
  engagementId: string;
  journeyInstanceId: string;
  talentId: string;
  trigger: EvidenceTrigger;
  triggerLabelKey: string;
  origin: EvidenceOrigin;
  decision: EvidenceDecision;
  /** The company's pipeline stage after the causing write. */
  pipelineStage: EvidencePipelineStage | null;
  /**
   * The consent head in force at decision time. `null` ONLY for a `backfill`
   * capture, where it was never recorded — never reconstructed.
   */
  consentScopes: ConsentScope[] | null;
  capturedAt: string;
  capturedByUserId: string | null;
  /** One entry per kind, in `EVIDENCE_KINDS` order — withheld markers included. */
  items: Array<{ id: string; kind: EvidenceKind; disposition: EvidenceDisposition }>;
}

export interface EvidenceCaptureView extends Omit<EvidenceCaptureSummary, 'items'> {
  items: EvidenceItemView[];
  retention: EvidenceRetentionView;
}

export interface EvidenceRetentionView {
  basis: EvidenceRetentionBasis;
  reasonKey: string;
  /** When the outcome that started the clock happened. Null while in force. */
  anchoredAt: string | null;
  /** Null while `decision_in_force` — the end date is not yet knowable. */
  retainUntil: string | null;
}

/**
 * D5 — the neutral comparison. `same` / `different` / `not_comparable` state a
 * fact about two values; nothing here says which is better, which direction a
 * number moved, or that anyone changed anything.
 */
export type EvidenceComparison = 'same' | 'different' | 'not_comparable';

export interface EvidenceDiffItem {
  kind: EvidenceKind;
  labelKey: string;
  consentScope: ConsentScope;
  atDecision: EvidenceItemView;
  current: {
    /** `withheld` when the CURRENT consent does not grant the scope. */
    disposition: 'captured' | 'withheld';
    payload: unknown;
    payloadSha256: string | null;
    viewerRedactedKeys: string[];
  };
  comparison: EvidenceComparison;
  /**
   * When the current value was last written, from the canonical reader's own
   * timestamp. Set only for `different`, and null where the reader carries no
   * timestamp — never guessed.
   */
  changedAt: string | null;
}

export interface EvidenceDiffView {
  captureId: string;
  capturedAt: string;
  comparedAt: string;
  items: EvidenceDiffItem[];
}

/** D3 — every verdict a company recorded, the current one flagged. */
export interface VerdictHistoryEntry {
  captureId: string;
  engagementId: string;
  journeyInstanceId: string;
  verdict: 'passed' | 'declined';
  replaced: 'passed' | 'declined' | null;
  decidedAt: string;
  decidedByUserId: string | null;
  origin: EvidenceOrigin;
  isCurrent: boolean;
}

export interface VerdictHistoryView {
  current: VerdictHistoryEntry | null;
  history: VerdictHistoryEntry[];
}

export interface EvidenceBlobUrlView {
  url: string;
  expiresInSeconds: number;
}

/**
 * Art. 12(4) — what a citizen is told when a delete does not reach a company's
 * decision record: WHICH company keeps WHAT, UNTIL WHEN, and WHY.
 */
export interface EvidenceRetentionNotice {
  companyTenantId: string;
  companyName: string;
  kinds: EvidenceKind[];
  kindLabelKeys: string[];
  basis: EvidenceRetentionBasis;
  reasonKey: string;
  /**
   * When the company's copy is disposed of.
   *
   * NON-null for every basis since W8.D, `decision_in_force` included — an open
   * engagement is a bounded period, not an absence of one. The type stays
   * nullable because a record whose clock has genuinely never been anchored
   * (a `backfill` capture the sweep has not reached) must be able to say so
   * rather than be given a date nobody computed.
   */
  retainUntil: string | null;
}
